<template>
  <div :class="$style.pageWrapper">
    <BreadCrumb :crumbs="crumbs" />

    <CreateOrEditCampaignForm v-if="displayedCampaign?.id !== 'noCampaign'" :campaign-id="displayedCampaign?.id" />

    <div :class="$style.headerSection">
      <InputText v-model="search" placeholder="Search offers" :class="$style.searchInput" @input="handleSearchOffers" />
      <Button
        label="New offer"
        icon="pi pi-plus"
        icon-pos="right"
        :disabled="!isWipVersion"
        @click="showCreateOfferModal"
      />
    </div>

    <ProgressSpinner v-if="!hasFilteredOffers" style="stroke: var(--vd-color-blue-default)" />
    <VTEmptyState
      v-else-if="searchedOffers.length === 0"
      :title="emptyState.title"
      :description="emptyState.description"
      :button-text="emptyState.buttonText"
      @empty-state-action="showCreateOfferModal"
    />
    <DataTable
      v-else
      :value="searchedOffers"
      data-key="id"
      table-style="min-width: 60rem; overflow: visible;"
      @row-click="(e) => redirectToOffer(e.data.id)"
    >
      <template #empty>
        <div>No offers found</div>
      </template>
      <Column field="name" header="Offer" style="width: 20%" />
      <Column header="Type">
        <template #body="slotProps">
          <span :class="$style.offerTypeTag">{{ typeLabel[slotProps.data.type] }}</span>
        </template>
      </Column>
      <Column header="Status">
        <template #body="slotProps">
          <OfferByStatus :status="slotProps.data.status" :show-label="true" :class="$style.statusChip" />
        </template>
      </Column>
      <Column header="Segments">
        <template #body="slotProps">
          <span :class="$style.offerSegmentTag">
            <i class="pi pi-users"></i>
            {{ getSegmentLabel(slotProps.data.conditions.segments) }}
          </span>
        </template>
      </Column>
      <Column header="Dates">
        <template #body="slotProps">
          <div :class="$style.offerDates">
            {{ formatDateTime(slotProps.data.startDate) }} - {{ formatDateTime(slotProps.data.endDate) }}
          </div>
        </template>
      </Column>
      <Column>
        <template #body="slotProps">
          <i :class="['pi pi-chevron-right', $style.redirectIcon]" @click="redirectToOffer(slotProps.data.id)"></i>
        </template>
      </Column>
    </DataTable>
  </div>
  <OfferForm
    :is-visible="offerModalVisible"
    :loading="creatingOffer"
    :campaign-id="campaignId"
    @save-offer="handleSaveOffer"
    @close="handleCreateOfferModalClose"
  />
</template>

<script setup lang="ts">
import { CreateOfferInput, useCreateOfferMutation } from "@/graphql";
import { useDebounceFn } from "@vueuse/core";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import BreadCrumb from "@/components/BreadCrumb/BreadCrumb.vue";
import useToast from "@/hooks/useToastNotification";
import CreateOrEditCampaignForm from "@/modules/Offers/components/CreateOrEditCampaignForm.vue";
import { useOffers } from "@/modules/Offers/hooks/useOffers";
import { useSegments } from "@/modules/Offers/useSegments";
import links from "@/router/links";
import formatDateTime from "@/utils/formatDateTime";
import OfferByStatus from "./components/OfferByStatus.vue";
import OfferForm from "./components/OfferForm.vue";
import { OfferItem } from "./types/types";

const router = useRouter();

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  campaignId: {
    type: String,
    required: false,
  },
});

const typeLabel: { [key: string]: string } = {
  ONE_PO: "1PO",
  TWO_PO: "2PO",
  THREE_PO: "3PO",
};

const { sortedCampaigns, refetchOffers, getCampaigns } = useOffers(props.gameId, props.versionId);
const { mutate: createOffer, loading: creatingOffer } = useCreateOfferMutation({});
const { segments } = useSegments(props.gameId);
const { toastSuccess, toastError } = useToast();

const displayedCampaign = computed(() => sortedCampaigns.value.find((campaign) => campaign.id === props.campaignId));
const isWipVersion = computed(() => props.versionId === "wip");
const offers = computed(() => sortedCampaigns.value.find((campaign) => campaign.id === props.campaignId)?.offers || []);

const offerModalVisible = ref(false);
const search = ref("");
const searchedOffers = ref<OfferItem[]>([]);
const hasFilteredOffers = ref(false);

const emptyState = {
  title: "No offers",
  description: "You have no offers for the time being, to create a new offer, use the button below",
  buttonText: "New offer",
};

const redirectToOffer = (offerId: string) => {
  router.push({
    name: links.offersAndStore.tabs.offerCampaigns.offerDetails,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      campaignId: props.campaignId,
      offerId,
    },
  });
};

const redirectToCampaignList = () => {
  router.push({ name: links.offersAndStore.tabs.offerCampaigns.campaignList });
};

const crumbs = computed(() => [
  { label: "Offer campaigns", action: redirectToCampaignList },
  ...(displayedCampaign.value ? [{ label: displayedCampaign.value.name }] : []),
]);

const getSegmentLabel = (segmentIds: string[] | undefined): string =>
  segments.value
    .filter((segment) => segmentIds?.includes(segment.id))
    .map((segment) => segment.name)
    .join(", ");

const handleSearchOffers = useDebounceFn((event) => {
  hasFilteredOffers.value = false;
  const searchValue = event.target.value;
  searchedOffers.value =
    offers.value?.filter((offer) => offer.name?.toLowerCase().includes(searchValue.toLowerCase())) || [];
  hasFilteredOffers.value = true;
}, 500);

watch([offers, search], () => {
  handleSearchOffers({ target: { value: search.value } });
});

const showCreateOfferModal = () => {
  offerModalVisible.value = true;
};

const handleCreateOfferModalClose = () => {
  offerModalVisible.value = false;
};

const handleSaveOffer = async (offer: CreateOfferInput) => {
  const res = await createOffer({
    appId: props.gameId,
    versionId: props.versionId,
    input: offer,
  });

  if (res?.data?.createOffer?.success) {
    handleCreateOfferModalClose();
    refetchOffers();
    toastSuccess({
      detail: `The offer ${offer.name} has been created successfully!`,
      summary: "Success",
      life: 3 * 1000,
    });
  } else {
    toastError({
      detail: res?.data?.createOffer?.details || "An error occurred while creating the offer.",
      life: 3 * 1000,
      summary: "Error",
    });
  }
};

onMounted(async () => {
  await getCampaigns();
});
</script>

<style module lang="scss">
.pageWrapper {
  display: flex;
  flex-direction: column;
}

.redirectIcon {
  color: var(--vd-color-text-light-2);
}

.offerTypeTag {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: var(--vd-color-blue-default);
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.offerDates {
  color: var(--vd-color-text-light-2);
}

.offerSegmentTag {
  padding: 3px 6px;
  border-radius: 20px;
  font-size: 11px;
  color: var(--vd-color-text-default);
  border: 1px solid var(--vd-color-blue-500);

  i {
    color: var(--vd-color-pink-500);
  }
}

.headerSection {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
}
</style>

<style scoped>
:deep(.p-datatable) {
  color: var(--vd-color-text-default);
  border: 1px solid var(--vd-color-text-light-7);
  border-bottom: 1px;
  border-radius: 8px;
}

:deep(.p-datatable .p-datatable-tbody > tr) {
  cursor: pointer;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  color: var(--vd-color-text-default);
  font-weight: 700;
  background: var(--vd-color-text-light-9);
}

:deep(.p-datatable-table-container) {
  border-radius: 8px;
}

:deep(.p-progressspinner) {
  height: 50vh !important;
}
</style>
